import {
  DATA_SUBJECT_REQUEST_STATUS_LABELS,
  DATA_SUBJECT_REQUEST_STATUSES,
  DATA_SUBJECT_REQUEST_CHANNEL_LABELS,
  DATA_SUBJECT_REQUEST_TYPE_LABELS,
  DATA_SUBJECT_REQUEST_TYPES,
  type DataSubjectRequest,
  dataSubjectRequestStatusSchema,
  dataSubjectRequestTypeSchema,
  DEADLINE_STATE_LABELS,
  DEADLINE_STATES,
  deadlineStateSchema,
} from "@station/contracts";
import { Alert, Button, DataTable, type DataTableColumn, EmptyState, FormField, Select } from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Plus, UserRoundSearch } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { MaskedNotice, PersonalValue } from "../../../../features/customers/personal-data.js";
import { requestDeadline } from "../../../../features/privacy/privacy-values.js";
import {
  type RequestFilters,
  requestsQuery,
  usePrivacyPermissions,
  usePrivacyRequests,
} from "../../../../features/privacy/queries.js";
import { RequestDialog } from "../../../../features/privacy/request-dialog.js";
import { DeadlineCell, PrivacyPage, RequestStatusBadge } from "../../../../features/privacy/shared.js";
import { compactSearch } from "../../../../features/shifts/shift-values.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../../lib/excel.js";
import { formatDate, formatDateTime } from "../../../../lib/format.js";

const searchSchema = z.object({
  status: dataSubjectRequestStatusSchema.optional().catch(undefined),
  deadline: deadlineStateSchema.optional().catch(undefined),
  type: dataSubjectRequestTypeSchema.optional().catch(undefined),
});

export const Route = createFileRoute("/_app/kvkk/basvurular/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: PrivacyRequestsRoute,
});

/**
 * İlgili kişi başvuruları (KVKK md. 11; ADR 0017 §9): son gün takibiyle liste. Son gün = alındığı İstanbul günü +
 * mevzuat parametresi (varsayılan 30) gününün sonu; kalan gün İSTANBUL takvim günüyle yazılır. Başvuranın adı /
 * iletişimi `privacy.request.manage` ya da `customers.personal.view` yoksa MASKELİ gelir.
 */
function PrivacyRequestsRoute(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePrivacyPermissions();
  const [creating, setCreating] = useState(false);
  return (
    <PrivacyPage
      title={t("menu.privacyRequests")}
      description={t("privacy.requests.description")}
      actions={
        permissions.canManage ? (
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("privacy.requests.new")}
          </Button>
        ) : null
      }
    >
      <RequestsBody />
      {permissions.canManage ? (
        <RequestDialog
          open={creating}
          onOpenChange={setCreating}
          onSaved={(saved) => void navigate({ to: "/kvkk/basvurular/$id", params: { id: saved.id } })}
        />
      ) : null}
    </PrivacyPage>
  );
}

function RequestsBody(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const filters: RequestFilters = {
    status: search.status,
    deadline: search.deadline,
    requestType: search.type,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = usePrivacyRequests(filters, pages.cursor);
  const rows = list.data?.items ?? [];

  const columns = useMemo<DataTableColumn<DataSubjectRequest>[]>(
    () => [
      {
        id: "requestNo",
        header: t("privacy.requests.fields.requestNo"),
        hideable: false,
        exportValue: (row) => row.requestNo,
        cell: (row) => (
          <Link
            to="/kvkk/basvurular/$id"
            params={{ id: row.id }}
            className="font-mono whitespace-nowrap underline underline-offset-4"
          >
            {row.requestNo}
          </Link>
        ),
      },
      {
        id: "requestType",
        header: t("privacy.requests.fields.requestType"),
        exportValue: (row) => DATA_SUBJECT_REQUEST_TYPE_LABELS[row.requestType],
        cell: (row) => <span>{DATA_SUBJECT_REQUEST_TYPE_LABELS[row.requestType]}</span>,
      },
      {
        id: "subject",
        header: t("privacy.requests.fields.subject"),
        exportValue: (row) => row.subjectName,
        cell: (row) => (
          <span className="grid gap-0.5">
            <PersonalValue value={row.subjectName} masked={row.personalDataMasked} />
            {row.customerCode ? (
              <span className="font-mono text-xs text-muted-foreground">{row.customerCode}</span>
            ) : null}
          </span>
        ),
      },
      {
        id: "channel",
        header: t("privacy.requests.fields.channel"),
        exportValue: (row) => DATA_SUBJECT_REQUEST_CHANNEL_LABELS[row.channel],
        cell: (row) => (
          <span className="whitespace-nowrap">{DATA_SUBJECT_REQUEST_CHANNEL_LABELS[row.channel]}</span>
        ),
      },
      {
        id: "receivedAt",
        header: t("privacy.requests.fields.receivedAt"),
        exportValue: (row) => formatDateTime(row.receivedAt),
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDateTime(row.receivedAt)}</span>,
      },
      {
        id: "dueAt",
        header: t("privacy.requests.fields.dueAt"),
        exportValue: (row) => {
          const info = requestDeadline(row);
          return info
            ? `${formatDate(row.dueAt)} (${DEADLINE_STATE_LABELS[info.state]})`
            : formatDate(row.dueAt);
        },
        cell: (row) => <DeadlineCell request={row} />,
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => DATA_SUBJECT_REQUEST_STATUS_LABELS[row.status],
        cell: (row) => <RequestStatusBadge status={row.status} />,
      },
      {
        id: "completedAt",
        header: t("privacy.requests.fields.completedAt"),
        defaultVisible: false,
        exportValue: (row) => (row.completedAt ? formatDateTime(row.completedAt) : null),
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDateTime(row.completedAt)}</span>,
      },
    ],
    [t],
  );

  return (
    <>
      <MaskedNotice masked={rows.some((row) => row.personalDataMasked)} />
      <DataTable
        caption={t("privacy.requests.caption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<UserRoundSearch />}
            title={t("privacy.requests.emptyTitle")}
            description={t("privacy.requests.emptyDescription")}
          />
        }
        filters={
          <>
            <FormField label={t("common.status")} className="w-40">
              {(control) => (
                <Select
                  {...control}
                  value={search.status ?? ""}
                  onChange={(event) => setSearch({ status: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {DATA_SUBJECT_REQUEST_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {DATA_SUBJECT_REQUEST_STATUS_LABELS[status]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("privacy.requests.fields.deadline")} className="w-48">
              {(control) => (
                <Select
                  {...control}
                  value={search.deadline ?? ""}
                  onChange={(event) => setSearch({ deadline: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {DEADLINE_STATES.map((state) => (
                    <option key={state} value={state}>
                      {DEADLINE_STATE_LABELS[state]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("privacy.requests.fields.requestType")} className="w-56">
              {(control) => (
                <Select
                  {...control}
                  value={search.type ?? ""}
                  onChange={(event) => setSearch({ type: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {DATA_SUBJECT_REQUEST_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {DATA_SUBJECT_REQUEST_TYPE_LABELS[type]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const all = await fetchAllPages<DataSubjectRequest>(
            async (cursor) =>
              (await api.GET("/privacy/requests", { params: { query: requestsQuery(filters, cursor, 200) } }))
                .data!,
          );
          await exportRowsToExcel({
            fileName: "kvkk-basvurular",
            sheetName: t("menu.privacyRequests"),
            columns: visible,
            rows: all,
          });
        }}
      />
      {search.deadline ? (
        <p className="text-xs text-muted-foreground">{t("privacy.requests.deadlineHint")}</p>
      ) : null}
    </>
  );
}
